// src/pages/RecentActivities.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/Layout/Header';
import { activitiesData } from '../data/activitiesData';
import './RecentActivities.css';

const RecentActivities = () => {
  const { activityId } = useParams();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    // Scroll to top when component loads
    window.scrollTo(0, 0);

    // Debug logs
    console.log('Activity ID from URL:', activityId);
    console.log('Available activities:', Object.keys(activitiesData));
    console.log('Looking for activity:', activitiesData[activityId]);

    // Load activity data
    if (activitiesData[activityId]) {
      console.log('Activity found!', activitiesData[activityId]);
      setActivity(activitiesData[activityId]);
      setLoading(false);
    } else {
      console.error('Activity not found for ID:', activityId);
      setLoading(false);
      // Redirect to home after 2 seconds if activity not found
      setTimeout(() => {
        console.log('Redirecting to home...');
        navigate('/');
      }, 2000);
    }
  }, [activityId, navigate]);

  useEffect(() => {
    // Scroll animation observer
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.scroll-animate');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [activity]);

  // Auto-slide gallery images
  useEffect(() => {
    if (!activity || !activity.gallery || activity.gallery.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % activity.gallery.length
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [activity]);

  const handleShare = (platform) => {
    const url = window.location.href;
    const text = `Check out: ${activity.title}`;
    
    switch(platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
        break;
      default:
        break;
    }
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? activity.gallery.length - 1 : prevIndex - 1
    );
  };

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      (prevIndex + 1) % activity.gallery.length
    );
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading activity details...</p>
        </div>
      </>
    );
  }

  if (!activity) {
    return (
      <>
        <Header />
        <div className="loading-container">
          <div className="error-message">
            <h2>❌ Activity Not Found</h2>
            <p>The activity you're looking for doesn't exist.</p>
            <p>Redirecting to home page...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="recent-activities-page">
      <div className="header-styled">
        <Header />
        <header className="activity-header">
          <div className="header-center">
            <div className="breadcrumb">
              <Link to="/">Home</Link> / <Link to="/#activities">Activities</Link> / <span>{activity.title}</span>
            </div>
            <h1>{activity.title}</h1>
            <div className="activity-meta-row">
              <span className="meta-badge">{activity.category}</span>
              <span className="meta-date">📅 {activity.date}</span>
              <span className="meta-location">📍 {activity.location}</span>
            </div>
          </div>
        </header>
      </div>

      <main className="activity-main">
        <div className="container">
          {/* Video Section */}
          {activity.videoUrl && (
            <section className="activity-section scroll-animate">
              <h2 className="section-title">Watch Video</h2>
              <div className="video-wrapper">
                <iframe
                  width="100%"
                  height="500"
                  src={activity.videoUrl}
                  title={activity.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </section>
          )}

          {/* Description Section */}
          <section className="activity-section scroll-animate">
            <h2 className="section-title">About This Activity</h2>
            <div className="content-card">
              <div className="activity-description">
                {activity.fullDescription.split('\n').map((paragraph, idx) => (
                  paragraph.trim() && <p key={idx}>{paragraph.trim()}</p>
                ))}
              </div>
            </div>
          </section>

          {/* Highlights Section */}
          {activity.highlights && activity.highlights.length > 0 && (
            <section className="activity-section scroll-animate">
              <h2 className="section-title">Key Highlights</h2>
              <div className="content-card highlights-card">
                <ul className="highlights-list">
                  {activity.highlights.map((highlight, idx) => (
                    <li key={idx}>
                      <span className="highlight-icon">✓</span>
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {/* Gallery Section with Carousel */}
          {activity.gallery && activity.gallery.length > 0 && (
            <section className="activity-section scroll-animate">
              <h2 className="section-title">Photo Gallery</h2>
              <div className="gallery-carousel">
                <button 
                  className="carousel-arrow left" 
                  onClick={prevImage}
                  aria-label="Previous image"
                >
                  ‹
                </button>
                <div className="gallery-main-image">
                  <img 
                    src={activity.gallery[currentImageIndex]} 
                    alt={`${activity.title} - ${currentImageIndex + 1}`}
                    onClick={() => setSelectedImage(activity.gallery[currentImageIndex])}
                    onError={(e) => {
                      console.error('Image failed to load:', activity.gallery[currentImageIndex]);
                      e.target.src = 'https://via.placeholder.com/800x500?text=Image+Not+Found';
                    }}
                  />
                  <div className="image-counter">
                    {currentImageIndex + 1} / {activity.gallery.length}
                  </div>
                </div>
                <button 
                  className="carousel-arrow right" 
                  onClick={nextImage}
                  aria-label="Next image"
                >
                  ›
                </button>
              </div>
              
              {/* Thumbnail Grid */}
              <div className="gallery-thumbnails">
                {activity.gallery.map((image, idx) => (
                  <div 
                    key={idx} 
                    className={`thumbnail ${idx === currentImageIndex ? 'active' : ''}`}
                    onClick={() => setCurrentImageIndex(idx)}
                  >
                    <img 
                      src={image} 
                      alt={`Thumbnail ${idx + 1}`}
                      onError={(e) => {
                        console.error('Thumbnail failed to load:', image);
                        e.target.src = 'https://via.placeholder.com/120x80?text=No+Image';
                      }}
                    />
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Organizer & Details Section */}
          <section className="activity-section scroll-animate">
            <h2 className="section-title">Event Details</h2>
            <div className="content-card organizer-card">
              <div className="organizer-info">
                <div className="info-item">
                  <span className="info-label">Organized By</span>
                  <span className="info-value">{activity.organizer}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Location</span>
                  <span className="info-value">📍 {activity.location}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Date</span>
                  <span className="info-value">📅 {activity.date}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Category</span>
                  <span className="info-value">{activity.category}</span>
                </div>
              </div>
              {activity.externalLink && (
                <a 
                  href={activity.externalLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="external-link-btn"
                >
                  Visit Official Website →
                </a>
              )}
            </div>
          </section>

          {/* Share Section */}
          <section className="activity-section scroll-animate">
            <h2 className="section-title">Share This Activity</h2>
            <div className="content-card share-card">
              <p className="share-text">Help spread the word about this initiative</p>
              <div className="share-buttons">
                <button 
                  className="share-btn facebook" 
                  onClick={() => handleShare('facebook')}
                  aria-label="Share on Facebook"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M22.675 0h-21.35C.597 0 0 .597 0 1.326v21.348C0 23.403.597 24 1.326 24H12.82v-9.294H9.692V11.41h3.128V8.797c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.464.099 2.797.143v3.24l-1.922.001c-1.506 0-1.797.717-1.797 1.767v2.31h3.587l-.467 3.296h-3.12V24h6.116C23.403 24 24 23.403 24 22.674V1.326C24 .597 23.403 0 22.675 0z"/>
                  </svg>
                  Facebook
                </button>
                <button 
                  className="share-btn twitter" 
                  onClick={() => handleShare('twitter')}
                  aria-label="Share on Twitter"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="20" height="20" fill="currentColor">
                    <path d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 26.8 48H172.4L272.9 180.9 389.2 48zM364.4 421.8h39.1L151.1 88h-42L364.4 421.8z"/>
                  </svg>
                  Twitter
                </button>
                <button 
                  className="share-btn whatsapp" 
                  onClick={() => handleShare('whatsapp')}
                  aria-label="Share on WhatsApp"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="20" height="20" fill="currentColor">
                    <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/>
                  </svg>
                  WhatsApp
                </button>
              </div>
            </div>
          </section>

          {/* Related Activities */}
          <section className="activity-section scroll-animate">
            <h2 className="section-title">Related Activities</h2>
            <div className="related-grid">
              {Object.values(activitiesData)
                .filter(act => act.id !== activity.id)
                .slice(0, 3)
                .map(relatedActivity => (
                  <a 
                    key={relatedActivity.id}
                    href={`/recent-activities/${relatedActivity.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="related-card"
                  >
                    <div className="related-image">
                      <img 
                        src={relatedActivity.thumbnail} 
                        alt={relatedActivity.title}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/400x200?text=No+Image';
                        }}
                      />
                      <span className="related-badge">{relatedActivity.category}</span>
                    </div>
                    <div className="related-content">
                      <h4>{relatedActivity.title}</h4>
                      <p className="related-date">📅 {relatedActivity.date}</p>
                      <p className="related-desc">{relatedActivity.shortDesc}</p>
                    </div>
                  </a>
                ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="activity-section scroll-animate">
            <div className="cta-card">
              <h2>Want to Support or Participate?</h2>
              <p>Join us in making a difference in the community</p>
              <div className="cta-buttons">
                <Link to="/donate" className="btn btn-primary">Donate Now</Link>
                <Link to="/contact" className="btn btn-secondary">Contact Us</Link>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Image Lightbox Modal */}
      {selectedImage && (
        <div className="lightbox-modal" onClick={() => setSelectedImage(null)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setSelectedImage(null)}>✕</button>
            <img src={selectedImage} alt="Full size" />
          </div>
        </div>
      )}
    </div>
  );
};

export default RecentActivities;